import React, { ReactNode, useCallback } from "react";
import dateHandler from "@/composables/dateUtil";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import {
  IMTSDocumentApprovalStatus,
  ImTsDocumentStatus,
  getIMTSDocumentApprovalStatusStyle,
  getIMTSDocumentStatusStyle,
  IMTSDocumentApprovalStatusText,
  IMTSDocumentStatusText,
  IMTSDocumentStatus,
} from "@/constants/enums/im-ts-document-status.enum";
import StatusChips, {
  StatusChipEnumType,
} from "@/components/common/StatusChips";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";

interface DocumentDetailsProps {
  document: DocumentDetail | null;
  type: DocumentType;
  isSignedDocument?: boolean;
}

export enum DocumentType {
  IM = "im",
  TS = "ts",
}

export interface DocumentDetail {
  id: number;
  created_by: {
    name: string;
  };
  status: number;
  submission_date: string;
  document_name: string;
  document_title: string;
  document_url: string;
  file_name: string;
  approval_status: IMTSDocumentApprovalStatus;
  approval_status_text: string;
  note?: string;
  rejected_by?: string;
  rejected_at?: string;
  created_at: string;
  updated_at: string;
  document_type: DocumentViewType;
  created_for?: string;
  sent_by?: string;
  received_on?: string;
  is_received?: boolean;
  client_name: string;
  associated_user_name?: string;
  super_trader_name?: string;
  uploaded_on?: string;
  rejected_on_date_and_time?: string;
  sent_on?: string;
  document_status?: ImTsDocumentStatus;
  document_type_text?: string;
  room_id?: number;
  sent_by_id?: number;
  sign_at?: string;
  is_term_sheet_created?: boolean;
  super_trader?: {
    id: number;
    user_type: UserTierType;
    email: string;
    name: string;
  };
  funder?: {
    id: number;
    user_type: UserTierType;
    email: string;
    name: string;
  };
}

export enum DocumentViewType {
  IM = 1,
  TS = 2,
}

enum DocumentDetailType {
  ClientName = "client_name",
  AssociatedUserName = "associated_user_name",
  SuperTraderName = "super_trader_name",
  UploadedOnDateAndTime = "uploaded_on",
  RejectedOnDateAndTime = "rejected_on_date_and_time",
  ReceivedOnDateAndTime = "received_on",
  CreatedOnDateAndTime = "created_at",
  SentOnDateAndTime = "sent_on",
  DocumentStatus = "document_status",
  ApprovalStatus = "approval_status",
  DocumentName = "document_name",
  CreatedFor = "created_for",
  SentBy = "sent_by",
  ReceivedOn = "received_on",
  ClientEntity = "name",
  DocumentType = "document_type",
  SubmissionDate = "submission_date",
  SignedDate = "signed_date",
}

const DocumentDetailTypeText: Record<DocumentDetailType, string> = {
  [DocumentDetailType.ClientName]: "Client Name (T4)",
  [DocumentDetailType.AssociatedUserName]: "Associate User Name",
  [DocumentDetailType.SuperTraderName]: "Super Trader (T4)",
  [DocumentDetailType.DocumentStatus]: "Document Status",
  [DocumentDetailType.ApprovalStatus]: "Approved Status",
  [DocumentDetailType.SentOnDateAndTime]: "Sent on Date & Time",
  [DocumentDetailType.RejectedOnDateAndTime]: "Rejected on Date & Time",
  [DocumentDetailType.ReceivedOnDateAndTime]: "Received on Date & Time",
  [DocumentDetailType.UploadedOnDateAndTime]: "Uploaded on Date & Time",
  [DocumentDetailType.CreatedOnDateAndTime]: "Created on Date & Time",
  [DocumentDetailType.DocumentName]: "Document Name",
  [DocumentDetailType.CreatedFor]: "Created For",
  [DocumentDetailType.SentBy]: "Sent By",
  [DocumentDetailType.ClientEntity]: "Client/Entity",
  [DocumentDetailType.DocumentType]: "Document Type",
  [DocumentDetailType.SubmissionDate]: "Submission Date",
  [DocumentDetailType.SignedDate]: "Signed Date",
};

type DocumentDetailsValues = {
  [key in DocumentDetailType]: string | number | undefined;
};

const DocumentDetails: React.FC<DocumentDetailsProps> = ({
  document,
  type,
  isSignedDocument = false,
}) => {
  const { formatDateWithTime } = dateHandler();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const renderDocumentDetails: () =>
    | Partial<DocumentDetailsValues>
    | undefined
    | null = useCallback(() => {
    if (!document) return null;

    if (type === DocumentType.IM) {
      if (user.user_type?.id === UserTierType.Tier3) {
        return {
          [DocumentDetailType.ClientName]: document.super_trader?.name,
          [DocumentDetailType.AssociatedUserName]: document.funder?.name,
          [DocumentDetailType.DocumentStatus]: document.document_status,
          [DocumentDetailType.ApprovalStatus]: document.approval_status,
          [DocumentDetailType.CreatedOnDateAndTime]: document.created_at,
          [DocumentDetailType.SentOnDateAndTime]: document.sent_on,
        };
      } else if (
        user.user_type?.id === UserTierType.Tier2 ||
        user.user_type?.id === UserTierType.Tier1
      ) {
        if (!isSignedDocument) {
          return {
            [DocumentDetailType.ClientName]: document.client_name,
            [DocumentDetailType.SentBy]: document.sent_by,
            [DocumentDetailType.ApprovalStatus]: document.approval_status,
            [DocumentDetailType.ReceivedOn]: document.received_on,
          };
        } else {
          return {
            [DocumentDetailType.ClientEntity]: document.super_trader?.name,
            [DocumentDetailType.SentBy]: document.sent_by,
            [DocumentDetailType.DocumentType]: document.document_type_text,
            [DocumentDetailType[
              document.approval_status === IMTSDocumentApprovalStatus.Approved
                ? "SignedDate"
                : "SubmissionDate"
            ]]:
              document[
                document.approval_status === IMTSDocumentApprovalStatus.Approved
                  ? "sign_at"
                  : "submission_date"
              ],
          };
        }
      }
    } else if (type === DocumentType.TS) {
      if (
        user.user_type?.id === UserTierType.Tier3 &&
        document.is_received === true
      ) {
        if (!isSignedDocument) {
          return {
            [DocumentDetailType.SuperTraderName]: document.super_trader?.name,
            [DocumentDetailType.SentBy]: document.sent_by,
            [DocumentDetailType.ApprovalStatus]: document.approval_status,
            [DocumentDetailType.UploadedOnDateAndTime]: document.uploaded_on,
            [DocumentDetailType.ReceivedOnDateAndTime]: document.received_on,
          };
        } else {
          return {
            [DocumentDetailType.ClientEntity]: document.super_trader?.name,
            [DocumentDetailType.SentBy]: document.sent_by,
            [DocumentDetailType.DocumentType]: document.document_type_text,
            [DocumentDetailType[
              document.approval_status === IMTSDocumentApprovalStatus.Approved
                ? "SignedDate"
                : "SubmissionDate"
            ]]:
              document[
                document.approval_status === IMTSDocumentApprovalStatus.Approved
                  ? "sign_at"
                  : "submission_date"
              ],
          };
        }
      } else if (
        user.user_type?.id === UserTierType.Tier3 &&
        document.is_received === false
      ) {
        return {
          [DocumentDetailType.SuperTraderName]: document.super_trader?.name,
          [DocumentDetailType.DocumentStatus]: document.document_status,
          [DocumentDetailType.ApprovalStatus]: document.approval_status,
          [DocumentDetailType.CreatedOnDateAndTime]: document.uploaded_on,
          [DocumentDetailType.SentOnDateAndTime]: document.received_on,
        };
      } else if (
        user.user_type?.id === UserTierType.Tier1 ||
        user.user_type?.id === UserTierType.Tier2
      ) {
        return {
          [DocumentDetailType.SentBy]: document.sent_by,
          [DocumentDetailType.ApprovalStatus]: document.approval_status,
          [DocumentDetailType.UploadedOnDateAndTime]: document.uploaded_on,
        };
      } else if (user.user_type?.id === UserTierType.Tier4) {
        if (!isSignedDocument) {
          return {
            [DocumentDetailType.SentBy]: document.sent_by,
            [DocumentDetailType.ApprovalStatus]: document.approval_status,
            [DocumentDetailType.UploadedOnDateAndTime]: document.uploaded_on,
          };
        } else {
          return {
            [DocumentDetailType.ClientEntity]: document.super_trader?.name,
            [DocumentDetailType.SentBy]: document.sent_by,
            [DocumentDetailType.DocumentType]: document.document_type_text,
            [DocumentDetailType.SubmissionDate]: document.submission_date,
          };
        }
      }
    }
  }, [document, user]);

  const getDocumentDetail = useCallback(
    (key: DocumentDetailType) => {
      const documentDetail = renderDocumentDetails() || {};
      const defaultNode = (data: ReactNode | string | number | undefined) => (
        <div>
          <p className="tw-text-sm tw-text-gray-500">
            {DocumentDetailTypeText[key]}
          </p>
          {[
            DocumentDetailType.ApprovalStatus,
            DocumentDetailType.DocumentStatus,
          ].includes(key) ? (
            <ul className="tw-list-none tw-p-0 tw-mt-2">
              <li
                className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${
                  key === DocumentDetailType.ApprovalStatus
                    ? getIMTSDocumentApprovalStatusStyle(
                        Number(
                          documentDetail[key],
                        ) as IMTSDocumentApprovalStatus,
                      ).bgColor +
                      " " +
                      getIMTSDocumentApprovalStatusStyle(
                        Number(
                          documentDetail[key],
                        ) as IMTSDocumentApprovalStatus,
                      ).textColor
                    : getIMTSDocumentStatusStyle(
                        Number(documentDetail[key]) as IMTSDocumentStatus,
                      ).bgColor +
                      " " +
                      getIMTSDocumentStatusStyle(
                        Number(documentDetail[key]) as IMTSDocumentStatus,
                      ).textColor
                }`}
              >
                <span
                  className={`tw-w-2 tw-h-2 tw-rounded-full ${
                    key === DocumentDetailType.ApprovalStatus
                      ? getIMTSDocumentApprovalStatusStyle(
                          Number(
                            documentDetail[key],
                          ) as IMTSDocumentApprovalStatus,
                        ).dotColor
                      : getIMTSDocumentStatusStyle(
                          Number(documentDetail[key]) as IMTSDocumentStatus,
                        ).dotColor
                  }`}
                />
                <span>
                  {key === DocumentDetailType.ApprovalStatus
                    ? IMTSDocumentApprovalStatusText[
                        Number(
                          documentDetail[key],
                        ) as IMTSDocumentApprovalStatus
                      ]
                    : IMTSDocumentStatusText[
                        Number(documentDetail[key]) as IMTSDocumentStatus
                      ]}
                </span>
              </li>
            </ul>
          ) : (
            <p className="tw-text-sm tw-font-medium">{data}</p>
          )}
        </div>
      );

      if (documentDetail[key] === undefined)
        return defaultNode(NO_DATA_AVAILABLE);

      if (
        [
          DocumentDetailType.CreatedOnDateAndTime,
          DocumentDetailType.SentOnDateAndTime,
          DocumentDetailType.RejectedOnDateAndTime,
          DocumentDetailType.ReceivedOnDateAndTime,
          DocumentDetailType.UploadedOnDateAndTime,
          DocumentDetailType.SubmissionDate,
          DocumentDetailType.SignedDate,
        ].includes(key)
      ) {
        if (documentDetail[key] === null || documentDetail[key] === undefined) {
          return defaultNode(NO_DATA_AVAILABLE);
        }
        return defaultNode(formatDateWithTime(String(documentDetail[key])));
      }

      return defaultNode(documentDetail[key]);
    },
    [renderDocumentDetails],
  );

  if (!document) return null;

  return (
    <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-md tw-border tw-border-[#E9EAEB]">
      <h3 className="tw-text-lg tw-font-semibold tw-mb-4">Document Details</h3>

      <div className="tw-space-y-4">
        {Object.keys(renderDocumentDetails() || {}).map((key, index) => (
          <div key={key + index}>
            {getDocumentDetail(key as DocumentDetailType)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentDetails;
